import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Package, Handshake, Star, XCircle, RotateCcw, Mail, X, MessageSquare, Wrench, FolderOpen } from 'lucide-react';
import api from '../api';

const NOTIFICATION_ICONS = {
  borrow_new_request: { icon: Mail, color: 'text-blue-500', bg: 'bg-blue-50' },
  borrow_approved: { icon: Check, color: 'text-green-500', bg: 'bg-green-50' },
  borrow_rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
  borrow_active: { icon: Handshake, color: 'text-purple-500', bg: 'bg-purple-50' },
  borrow_returned: { icon: RotateCcw, color: 'text-blue-500', bg: 'bg-blue-50' },
  borrow_cancelled: { icon: XCircle, color: 'text-gray-500', bg: 'bg-gray-50' },
  borrow_agreement_confirmed: { icon: CheckCheck, color: 'text-green-500', bg: 'bg-green-50' },
  borrow_rated: { icon: Star, color: 'text-amber-500', bg: 'bg-amber-50' },
  new_message: { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50' },
  new_tool: { icon: Wrench, color: 'text-[#004B00]', bg: 'bg-green-50' },
  new_project: { icon: FolderOpen, color: 'text-orange-500', bg: 'bg-orange-50' },
  default: { icon: Package, color: 'text-gray-500', bg: 'bg-gray-50' },
};

export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications?limit=10'),
        api.get('/notifications/unread-count'),
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getIconConfig = (type) => {
    return NOTIFICATION_ICONS[type] || NOTIFICATION_ICONS.default;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleOpen}
        data-testid="notification-bell"
        className="relative p-2 rounded-full hover:bg-stone-100 transition-colors"
      >
        <Bell size={22} className="text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="fixed left-3 right-3 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96 bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden z-50" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-stone-50 border-b border-stone-100">
            <h3 className="font-poppins font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-[#004B00] font-semibold hover:underline font-nunito"
                data-testid="mark-all-read"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 px-4 text-center">
                <Bell size={32} className="text-stone-300 mx-auto mb-3" />
                <p className="text-gray-500 font-nunito text-sm">No notifications yet</p>
                <p className="text-gray-400 font-nunito text-xs mt-1">
                  You'll be notified when something happens
                </p>
              </div>
            ) : (
              notifications.map((notif) => {
                const iconConfig = getIconConfig(notif.type);
                const Icon = iconConfig.icon;
                const inner = (
                  <div
                    key={notif.id}
                    className={`flex gap-3 px-4 py-3 hover:bg-stone-50 transition-colors border-b border-stone-50 last:border-b-0 cursor-pointer ${
                      !notif.read ? 'bg-blue-50/30' : ''
                    }`}
                    data-testid={`notification-${notif.id}`}
                    onClick={() => {
                      if (!notif.read) markAsRead(notif.id);
                      if (notif.link) { setIsOpen(false); navigate(notif.link); }
                    }}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconConfig.bg}`}>
                      <Icon size={18} className={iconConfig.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-nunito ${!notif.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {notif.title}
                        </p>
                        {!notif.read && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markAsRead(notif.id); }}
                            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                            title="Mark as read"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 font-nunito line-clamp-2 mt-0.5">
                        {notif.message}
                      </p>
                      <p className="text-xs text-gray-400 font-nunito mt-1">
                        {formatTime(notif.created_at)}
                      </p>
                    </div>
                    {!notif.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                    )}
                  </div>
                );
                return inner;
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 bg-stone-50 border-t border-stone-100">
              <Link
                to="/borrow-requests"
                onClick={() => setIsOpen(false)}
                className="block text-center text-sm text-[#004B00] font-semibold font-nunito hover:underline"
              >
                View all borrow requests
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
